import dayjs, { Dayjs } from 'dayjs';

import {
  ApiCommEvent,
  ApiInvoice,
  ApiLease,
  ApiPayment,
  ApiProperty,
  ApiReminderAction,
  ApiReminderTemplate,
  ApiTenant,
  ApiUserMeta,
} from '~shared/types';

export const PRODUCT_DEMO_IDS = {
  property: 9_000_001,
  tenant: 9_000_002,
  lease: 9_000_003,
  invoicePaid: 9_000_004,
  invoicePlanned: 9_000_005,
  invoiceOverdue: 9_000_006,
  invoiceDeposit: 9_000_016,
  paymentConfirmed: 9_000_007,
  reminderTemplateRent: 9_000_008,
  reminderTemplateMeters: 9_000_009,
  reminderActionSent: 9_000_010,
  reminderActionPlanned: 9_000_011,
  commEventSent: 9_000_012,
  userMeta: 9_000_013,
  tenantConnected: 9_000_015,
  leaseConnected: 9_000_017,
} as const;

type ProductDemoData = {
  userMeta: ApiUserMeta;
  properties: ApiProperty[];
  tenants: ApiTenant[];
  leases: ApiLease[];
  invoices: ApiInvoice[];
  payments: ApiPayment[];
  reminderTemplates: ApiReminderTemplate[];
  reminderActions: ApiReminderAction[];
  commEvents: ApiCommEvent[];
  tenantInviteLink: string;
};

const toIso = (date: Dayjs) => date.toISOString();

const clampDay = (day: number) => Math.min(Math.max(day, 1), 28);

const base = (id: number, now: Dayjs) => ({
  id,
  created_at: toIso(now.subtract(10, 'day')),
  updated_at: toIso(now.subtract(1, 'hour')),
});

export const createProductDemoData = ({
  now = dayjs(),
  userId = PRODUCT_DEMO_IDS.userMeta,
}: {
  now?: Dayjs;
  userId?: number;
} = {}): ProductDemoData => {
  const rentDueDate = now.add(4, 'day');
  const meterReminderDate = now.add(2, 'day').hour(10).minute(0).second(0);
  const sentReminderDate = now.subtract(1, 'day').hour(9).minute(0).second(0);
  const paidInvoiceDate = now.subtract(1, 'month').date(clampDay(now.date()));

  const property: ApiProperty = {
    ...base(PRODUCT_DEMO_IDS.property, now),
    name: 'Тестова квартира на Подолі',
    description:
      'Тестовий обʼєкт, який показує, як виглядає заповнений кабінет Rentify.',
    address: {
      country: 'Україна',
      region: 'Київська область',
      city: 'Київ',
      street: 'Тестова адреса',
      building: '12',
      apartment: '34',
    },
    area: 52,
    room_count: 2,
    photos: [],
    type: 'residential',
    sub_type: 'apartment',
    preferred_rent_type: 'long-term',
    default_month_rent_price: 18000,
    default_rent_currency: 'UAH',
    lease_ids: [PRODUCT_DEMO_IDS.lease, PRODUCT_DEMO_IDS.leaseConnected],
    lessor_id: userId,
    status: 'occupied',
  };

  const tenant: ApiTenant = {
    ...base(PRODUCT_DEMO_IDS.tenant, now),
    lease_ids: [PRODUCT_DEMO_IDS.lease],
    firstname: 'Орендар 01',
    lastname: null,
    patronymic: null,
    email: null,
    phone: '+380501112233',
    primary: true,
    user_id: null,
    verified_phone: null,
    lessor_id: userId,
    property_ids: [PRODUCT_DEMO_IDS.property],
    connection_status: 'pending',
    lease_participation_status: 'active',
  };

  const tenantConnected: ApiTenant = {
    ...base(PRODUCT_DEMO_IDS.tenantConnected, now),
    lease_ids: [PRODUCT_DEMO_IDS.leaseConnected],
    firstname: 'Орендар 02',
    lastname: null,
    patronymic: null,
    email: null,
    phone: '+380501112255',
    primary: false,
    user_id: PRODUCT_DEMO_IDS.tenantConnected,
    verified_phone: '+380501112255',
    lessor_id: userId,
    property_ids: [PRODUCT_DEMO_IDS.property],
    connection_status: 'active',
    lease_participation_status: 'active',
  };

  const lease: ApiLease = {
    ...base(PRODUCT_DEMO_IDS.lease, now),
    property_id: PRODUCT_DEMO_IDS.property,
    tenant_id: PRODUCT_DEMO_IDS.tenant,
    start_date: toIso(now.subtract(2, 'month').startOf('month')),
    end_date: toIso(now.add(10, 'month').endOf('month')),
    auto_renewal: true,
    status: 'active',
    payment_method: 'bank',
    currency: 'UAH',
    auto_send_access_link: false,
    schedule: {
      rent: {
        period: 'monthly',
        due: clampDay(rentDueDate.date()),
        price: 18000,
      },
      meter_reading: {
        enabled: true,
        due: clampDay(meterReminderDate.date()),
      },
    },
    reminders: {
      rent: {
        enabled: true,
        time: '09:00',
        channels: ['sms'],
        offsets: [-3, 0],
        repeat: true,
      },
      meter_reading: {
        enabled: true,
        time: '10:00',
        channels: ['sms'],
        offsets: [-2],
        repeat: false,
      },
      utility: {
        enabled: false,
        time: '10:00',
        channels: ['sms'],
        offsets: [],
        repeat: false,
      },
    },
    deposit_amount: 18000,
    deposit_status: 'held',
    utilities_included: false,
    notes:
      'Тестова оренда. Дані не зберігаються, повідомлення не надсилаються.',
    tenant_connection_status: 'pending',
    tenant_connected_at: null,
    lessor_id: userId,
    name: 'Тестова квартира на Подолі',
    code: 'TEST-L-001',
    property_type: 'residential',
    timezone: 'Europe/Kiev',
    archived_at: null,
    finished_at: null,
    deleted_at: null,
  };

  const leaseConnected: ApiLease = {
    ...base(PRODUCT_DEMO_IDS.leaseConnected, now),
    property_id: PRODUCT_DEMO_IDS.property,
    tenant_id: PRODUCT_DEMO_IDS.tenantConnected,
    start_date: toIso(now.subtract(1, 'month').startOf('month')),
    end_date: toIso(now.add(11, 'month').endOf('month')),
    auto_renewal: true,
    status: 'active',
    payment_method: 'bank',
    currency: 'UAH',
    auto_send_access_link: false,
    schedule: {
      rent: {
        period: 'monthly',
        due: clampDay(rentDueDate.date()),
        price: 16500,
      },
      meter_reading: {
        enabled: false,
        due: clampDay(meterReminderDate.date()),
      },
    },
    reminders: {
      rent: {
        enabled: true,
        time: '09:00',
        channels: ['sms'],
        offsets: [-3],
        repeat: true,
      },
      meter_reading: {
        enabled: false,
        time: '10:00',
        channels: ['sms'],
        offsets: [],
        repeat: false,
      },
      utility: {
        enabled: false,
        time: '10:00',
        channels: ['sms'],
        offsets: [],
        repeat: false,
      },
    },
    deposit_amount: 16500,
    deposit_status: 'held',
    utilities_included: false,
    notes:
      'Тестовий підключений договір. Дані не зберігаються, повідомлення не надсилаються.',
    tenant_connection_status: 'active',
    tenant_connected_at: toIso(now.subtract(5, 'day')),
    lessor_id: userId,
    name: 'Тестовий підключений договір',
    code: 'TEST-L-002',
    property_type: 'residential',
    timezone: 'Europe/Kiev',
    archived_at: null,
    finished_at: null,
    deleted_at: null,
  };

  const invoices: ApiInvoice[] = [
    {
      ...base(PRODUCT_DEMO_IDS.invoicePaid, now),
      created_at: toIso(now.subtract(4, 'day')),
      name: `Оренда за ${paidInvoiceDate.format('MMMM')}`,
      lease_id: PRODUCT_DEMO_IDS.lease,
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      property_id: PRODUCT_DEMO_IDS.property,
      type: 'rent',
      amount: 18000,
      currency: 'UAH',
      due_date: toIso(paidInvoiceDate),
      status: 'completed',
      remaining_amount: 0,
      property_type: 'residential',
      code: 'TEST-R-001',
      lessor_id: userId,
      payment_status: 'paid',
      paid_at: [toIso(paidInvoiceDate.add(1, 'day'))],
      created_by: 'system',
      archived_at: null,
      status_draft: null,
    },
    {
      ...base(PRODUCT_DEMO_IDS.invoicePlanned, now),
      created_at: toIso(now.subtract(2, 'hour')),
      name: `Оренда за ${rentDueDate.format('MMMM')}`,
      lease_id: PRODUCT_DEMO_IDS.lease,
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      property_id: PRODUCT_DEMO_IDS.property,
      type: 'rent',
      amount: 18000,
      currency: 'UAH',
      due_date: toIso(rentDueDate),
      status: 'planned',
      remaining_amount: 18000,
      property_type: 'residential',
      code: 'TEST-R-002',
      lessor_id: userId,
      payment_status: 'unpaid',
      paid_at: [],
      created_by: 'system',
      archived_at: null,
      status_draft: null,
    },
    {
      ...base(PRODUCT_DEMO_IDS.invoiceOverdue, now),
      created_at: toIso(now.subtract(90, 'minute')),
      name: 'Комунальні послуги',
      lease_id: PRODUCT_DEMO_IDS.lease,
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      property_id: PRODUCT_DEMO_IDS.property,
      type: 'utility',
      amount: 2400,
      currency: 'UAH',
      due_date: toIso(now.subtract(3, 'day')),
      status: 'overdue',
      remaining_amount: 2400,
      property_type: 'residential',
      code: 'TEST-U-001',
      lessor_id: userId,
      payment_status: 'unpaid',
      paid_at: [],
      created_by: 'system',
      archived_at: null,
      status_draft: null,
    },
    {
      ...base(PRODUCT_DEMO_IDS.invoiceDeposit, now),
      created_at: toIso(now.subtract(1, 'hour')),
      name: `Оренда за ${rentDueDate.format('MMMM')}`,
      lease_id: PRODUCT_DEMO_IDS.lease,
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      property_id: PRODUCT_DEMO_IDS.property,
      type: 'rent',
      amount: 18000,
      currency: 'UAH',
      due_date: toIso(now.add(1, 'day')),
      status: 'planned',
      remaining_amount: 18000,
      property_type: 'residential',
      code: 'TEST-R-003',
      lessor_id: userId,
      payment_status: 'unpaid',
      paid_at: [],
      created_by: 'system',
      archived_at: null,
      status_draft: null,
    },
  ];

  const payments: ApiPayment[] = [
    {
      ...base(PRODUCT_DEMO_IDS.paymentConfirmed, now),
      created_at: toIso(now.subtract(30, 'minute')),
      type: 'rent',
      invoice_id: PRODUCT_DEMO_IDS.invoicePaid,
      payment_date: toIso(paidInvoiceDate.add(1, 'day')),
      payment_method: 'bank',
      amount: 18000,
      comments: 'Тестова оплата за попередній місяць',
      lease_id: PRODUCT_DEMO_IDS.lease,
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      status: 'confirmed',
      code: 'TEST-P-001',
      property_id: PRODUCT_DEMO_IDS.property,
      receipt: null,
      created_by: 'tenant',
      confirmed_at: toIso(paidInvoiceDate.add(1, 'day')),
      confirmed_by: 'lessor',
      rejected_at: null,
      rejected_by: null,
    },
  ];

  const reminderTemplates: ApiReminderTemplate[] = [
    {
      ...base(PRODUCT_DEMO_IDS.reminderTemplateRent, now),
      title: 'Нагадування про оплату',
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      lease_id: PRODUCT_DEMO_IDS.lease,
      is_active: true,
      type: 'rent',
      channels: ['sms'],
      offsets: [-3, 0],
      time: '09:00',
      lessor_id: userId,
      property_id: PRODUCT_DEMO_IDS.property,
      unique_key: 'demo-rent-reminder',
    },
    {
      ...base(PRODUCT_DEMO_IDS.reminderTemplateMeters, now),
      title: 'Нагадування про показники',
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      lease_id: PRODUCT_DEMO_IDS.lease,
      is_active: true,
      type: 'meter_reading',
      channels: ['sms'],
      offsets: [-2],
      time: '10:00',
      lessor_id: userId,
      property_id: PRODUCT_DEMO_IDS.property,
      unique_key: 'demo-meter-reminder',
    },
  ];

  const reminderActions: ApiReminderAction[] = [
    {
      ...base(PRODUCT_DEMO_IDS.reminderActionSent, now),
      template_id: PRODUCT_DEMO_IDS.reminderTemplateRent,
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      lease_id: PRODUCT_DEMO_IDS.lease,
      property_id: PRODUCT_DEMO_IDS.property,
      scheduled_at: toIso(sentReminderDate),
      type: 'rent',
      subject_type: 'invoice',
      subject_id: PRODUCT_DEMO_IDS.invoicePlanned,
      channels: ['sms'],
      offset_type: 'before',
      lessor_id: userId,
      sent_at: toIso(sentReminderDate),
      status: 'sent',
      unique_key: 'demo-sent-rent-action',
      offset: -3,
      proof: null,
    },
    {
      ...base(PRODUCT_DEMO_IDS.reminderActionPlanned, now),
      template_id: PRODUCT_DEMO_IDS.reminderTemplateMeters,
      tenant_id: PRODUCT_DEMO_IDS.tenant,
      lease_id: PRODUCT_DEMO_IDS.lease,
      property_id: PRODUCT_DEMO_IDS.property,
      scheduled_at: toIso(meterReminderDate),
      type: 'meter_reading',
      subject_type: 'meter_reading',
      subject_id: null,
      channels: ['sms'],
      offset_type: 'on',
      lessor_id: userId,
      sent_at: null,
      status: 'planned',
      unique_key: 'demo-planned-meter-action',
      offset: 0,
      proof: null,
    },
  ];

  const commEvents: ApiCommEvent[] = [
    {
      ...base(PRODUCT_DEMO_IDS.commEventSent, now),
      channels: [
        {
          type: 'sms',
          status: 'delivered',
          fail_reason: null,
          render_data: null,
          target: null,
          external_message_id: null,
          last_checked_at: toIso(sentReminderDate.add(5, 'minute')),
          provider: 'demo',
          retry_count: 0,
        },
      ],
      reminder_action_id: PRODUCT_DEMO_IDS.reminderActionSent,
      reminder_template_id: PRODUCT_DEMO_IDS.reminderTemplateRent,
      due_date: toIso(rentDueDate),
      type: 'rent',
      sub_type: 'action',
      notification_callback: 'lessor_rent_sent',
      notification_params: [],
      recipient_type: 'tenant',
      recipient_id: PRODUCT_DEMO_IDS.tenant,
      subject_type: 'invoice',
      subject_id: PRODUCT_DEMO_IDS.invoicePlanned,
    },
  ];

  return {
    userMeta: {
      ...base(PRODUCT_DEMO_IDS.userMeta, now),
      user_id: userId,
      login_count: 1,
      last_activity_at: toIso(now),
      first_action_at: toIso(now.subtract(10, 'day')),
      lessor: {
        onboarding_started_at: toIso(now.subtract(10, 'day')),
        onboarding_completed_at: toIso(now.subtract(9, 'day')),
        completion_screen_dismissed_at: toIso(now.subtract(9, 'day')),
        property_added_at: toIso(now.subtract(10, 'day')),
        lease_added_at: toIso(now.subtract(9, 'day')),
        reminders_prepared_at: toIso(now.subtract(9, 'day')),
        aha_moment_reached_at: toIso(now.subtract(8, 'day')),
        outreach_ref: null,
      },
      tenant: null,
    },
    properties: [property],
    tenants: [tenant, tenantConnected],
    leases: [lease, leaseConnected],
    invoices,
    payments,
    reminderTemplates,
    reminderActions,
    commEvents,
    tenantInviteLink: 'https://app.rentify.com.ua/tenant/connect?token=demo',
  };
};
